import { useEffect, useState } from 'react';
import type { ThreeCardPokerLegal, ThreeCardPokerView } from '@cardhub/engine';
import { PlayingCard } from '../cards/PlayingCard';
import { Coins } from '../components/ui';
import { useT, type MessageKey } from '../i18n';
import { useGame } from '../store/game';
import { MySeat, useNarrow } from './common';

export function ThreeCardPokerTable() {
  const t = useT();
  const view = useGame((s) => s.view) as ThreeCardPokerView;
  const legal = useGame((s) => s.legal) as ThreeCardPokerLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const acting = useGame((s) => s.acting);
  const narrow = useNarrow();
  const name = (s: number) => seats[s]?.user?.username ?? `Seat ${s + 1}`;
  const myTurn = mySeat !== null && turn?.seats.includes(mySeat);
  const me = mySeat !== null ? view.players[mySeat] : null;
  const betting = Boolean(myTurn && legal?.kind === 'bet');
  const deciding = Boolean(myTurn && legal?.kind === 'decide');
  const [ante, setAnte] = useState<number>(view.config.minBet);
  const [pairPlus, setPairPlus] = useState<number>(0);
  const ended = view.phase === 'HAND_END';
  const result = view.lastResult;
  const myResult = ended ? result?.seats.find((r) => r.seat === mySeat) : undefined;
  const cat = (c: string) => t(`tcp.cat.${c}` as MessageKey);

  useEffect(() => {
    if (legal?.kind === 'bet') {
      setAnte((a) => Math.max(legal.min, Math.min(a, legal.max)));
      setPairPlus((p) => (p > legal.pairPlusMax ? 0 : p));
    }
  }, [legal, view.handNo]);

  const quick = (min: number, max: number) => [...new Set([1, 2, 5, 10, 25].map((k) => min * k).filter((v) => v <= max).concat(max))].sort((a, b) => a - b);
  const chip = (v: number, on: boolean, pick: () => void) => (
    <button key={v} role="radio" aria-checked={on} className={`bid-chip ${on ? 'on' : ''}`} onClick={pick}>
      {v}
    </button>
  );
  // The pair plus bet comes out of what is left after the ante and the play bet.
  const room = legal?.kind === 'bet' && me ? Math.max(0, Math.min(view.config.maxBet, me.chips - 2 * ante)) : 0;
  const pairPlusOptions = legal?.kind === 'bet' ? quick(legal.min, Math.max(legal.min, room)).filter((v) => v <= room) : [];
  const shownPairPlus = pairPlus <= room ? pairPlus : 0;

  const cardRow = (cards: string[], hidden: boolean, label: string) => (
    <div style={{ display: 'flex', gap: 4, justifyContent: 'center', minHeight: narrow ? 52 : 68 }} aria-label={label}>
      {[0, 1, 2].map((i) => (cards[i] && !hidden ? <PlayingCard key={i} id={cards[i]!} size="sm" /> : <PlayingCard key={i} id="AS" size="sm" faceDown />))}
    </div>
  );

  const dealerHidden = !ended;
  const dealtIn = view.players.some((p) => p && p.hasCards);

  return (
    <>
      <div className="stage">
        <div className="felt" />
        <div className="casino-layer">
          <div className="card glass col" style={{ padding: 8, gap: 6, alignItems: 'center' }}>
            <strong>{t('tcp.dealer')}</strong>
            {dealtIn || ended ? cardRow(view.dealer, dealerHidden, t('tcp.dealer')) : <span className="dim">–</span>}
            {ended && result && (
              <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>
                {cat(result.dealerCategory)} · {result.qualified ? t('tcp.dealerQualifies') : t('tcp.noQualify')}
              </span>
            )}
          </div>
          <div className="row" style={{ justifyContent: 'center', gap: 8, minHeight: 28, flexWrap: 'wrap' }}>
            {ended && myResult && (
              <span className={`pill ${myResult.delta > 0 ? 'accent' : myResult.delta < 0 ? 'danger' : ''}`} role="status">
                {myResult.delta > 0 ? t('ab.youWon', { n: myResult.delta }) : myResult.delta < 0 ? t('ab.youLost', { n: -myResult.delta }) : t('bc.push')}
              </span>
            )}
            {ended && myResult && myResult.anteBonus > 0 && <span className="pill accent">{t('tcp.bonus', { n: myResult.anteBonus })}</span>}
            {ended && myResult && myResult.pairPlusWin > 0 && <span className="pill accent">{t('tcp.ppWin', { n: myResult.pairPlusWin })}</span>}
            {view.phase === 'DEALING' && <span className="center-note">{t('ab.dealing')}</span>}
            {view.phase === 'REVEAL' && <span className="center-note">{t('tcp.revealing')}</span>}
            {view.phase === 'WAITING' && <span className="center-note">{t('bj.waiting')}</span>}
            {(view.phase === 'BETTING' || view.phase === 'DECIDING') && !myTurn && turn?.seats[0] !== undefined && <span className="center-note">{t('table.waitingFor', { name: name(turn.seats[0]) })}</span>}
          </div>
          {view.hand.length === 3 && (
            <div className="card glass col" style={{ padding: 8, gap: 6, alignItems: 'center' }}>
              <strong>{t('tcp.yourHand')}</strong>
              {cardRow(view.hand, false, t('tcp.yourHand'))}
              {view.myCategory && <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{cat(view.myCategory)}</span>}
            </div>
          )}
          {view.players.some((p) => p && p.status === 'BET') && <div className="card glass col" style={{ padding: 8, gap: 4 }} aria-label={t('bc.bets')}>
            {view.players.map((p) => {
              if (!p || p.status !== 'BET') return null;
              const r = ended ? result?.seats.find((x) => x.seat === p.seat) : undefined;
              return (
                <div key={p.seat} className="row" style={{ gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{ fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.8)', minWidth: 64 }}>{p.seat === mySeat ? t('cg.you') : name(p.seat)}</span>
                  <span className="pill" style={{ fontSize: 'var(--fs-xs)' }}>
                    {t('tcp.ante')} {p.ante}
                    {p.play > 0 ? ` + ${p.play}` : ''}
                  </span>
                  {p.pairPlus > 0 && <span className="pill" style={{ fontSize: 'var(--fs-xs)' }}>{t('tcp.pairPlus')} {p.pairPlus}</span>}
                  {p.decision === 'FOLD' && <span className="pill danger" style={{ fontSize: 'var(--fs-xs)' }}>{t('tcp.folded')}</span>}
                  {r && (
                    <span className={`pill ${r.delta > 0 ? 'accent' : r.delta < 0 ? 'danger' : ''}`} style={{ fontSize: 'var(--fs-xs)' }}>
                      {r.cards ? `${cat(r.category)} · ` : ''}
                      {r.delta > 0 ? `+${r.delta}` : r.delta}
                    </span>
                  )}
                </div>
              );
            })}
          </div>}
        </div>
        <MySeat seat={mySeat !== null ? seats[mySeat] : undefined} sub={me ? <Coins amount={me.chips} size={14} /> : null} />
      </div>
      <div className="table-bottom">
        {betting && legal?.kind === 'bet' && (
          <div className="bid-picker" role="group" aria-label={t('bj.placeBet')}>
            <div className="bid-head">
              <strong>{t('tcp.antePlay')}</strong>
              <span className="dim">{t('bj.limits', { min: legal.min, max: legal.max })}</span>
            </div>
            <div className="bid-chips amounts" role="radiogroup" aria-label={t('tcp.ante')}>
              {quick(legal.min, legal.max).map((v) => chip(v, ante === v, () => setAnte(v)))}
            </div>
            {pairPlusOptions.length > 0 && (
              <>
                <div className="bid-head">
                  <strong>{t('tcp.pairPlusBet')}</strong>
                </div>
                <div className="bid-chips amounts" role="radiogroup" aria-label={t('tcp.pairPlus')}>
                  {chip(0, shownPairPlus === 0, () => setPairPlus(0))}
                  {pairPlusOptions.map((v) => chip(v, shownPairPlus === v, () => setPairPlus(v)))}
                </div>
              </>
            )}
            <div className="row" style={{ gap: 8 }}>
              {legal.canSitOut && (
                <button className="btn grow" disabled={acting} onClick={() => void act({ type: 'sitOut' })}>
                  {t('bj.sitOut')}
                </button>
              )}
              <button className="btn primary grow bid-confirm" disabled={acting} onClick={() => void act({ type: 'bet', ante, pairPlus: shownPairPlus })}>
                {shownPairPlus > 0 ? t('tcp.betPP', { n: ante, pp: shownPairPlus }) : t('tcp.bet', { n: ante })}
              </button>
            </div>
          </div>
        )}
        {deciding && legal?.kind === 'decide' && (
          <div className="action-bar" role="group" aria-label={t('tcp.decide')}>
            <button className="btn grow" disabled={acting} onClick={() => void act({ type: 'fold' })}>
              {t('tcp.fold')}
            </button>
            <button className="btn primary grow" disabled={acting} onClick={() => void act({ type: 'play' })}>
              {t('tcp.play', { n: legal.play })}
            </button>
          </div>
        )}
        {!betting && !deciding && (
          <div className="action-bar">
            <span style={{ fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.8)' }}>
              {me && me.status === 'BET' && !ended ? t('tcp.youBet', { n: me.ante }) : ended ? t('bj.handOver') : view.phase === 'BETTING' ? t('bj.betting') : view.phase === 'DECIDING' ? t('tcp.wait') : t('bj.waiting')}
            </span>
          </div>
        )}
      </div>
    </>
  );
}
